import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import InputMask from 'inputmask-core'
import { List } from 'immutable'
import { PureDataComponent } from '@aytm/lib/common/components/pure-component'
import { stopEvent, isKeyUndo, isKeyRedo, getSelection, setSelection } from '@aytm/lib/common/func'
import { KEYCODE_BACKSPACE } from '@aytm/lib/common/const'

import '@aytm/lib/rfd-ui/styles/basic/masked-input.scss'

export default class MaskedInput extends PureDataComponent {
  static propTypes = {
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    pattern: PropTypes.string,
    placeholderChar: PropTypes.string,
    formatCharacters: PropTypes.object,
    sub: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mono: PropTypes.bool,
    onKeyPress: PropTypes.func,
    onKeyDown: PropTypes.func,
    onChange: PropTypes.func,
    onPaste: PropTypes.func
  }

  constructor(props) {
    super(props)
    this.updateSelection = this.updateSelection.bind(this)
    this.updateValue = this.updateValue.bind(this)
    this.setMask = this.setMask.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onKeyPress = this.onKeyPress.bind(this)
    this.onPaste = this.onPaste.bind(this)
    this.onChange = this.onChange.bind(this)
    this.updatePattern = this.updatePattern.bind(this)
    this.state = {
      value: ''
    }
  }

  updateSelection() {
    const { maskLib, refInput } = this
    if (refInput && maskLib) {
      maskLib.setSelection(getSelection(refInput))
    }
  }

  updateValue(sendUpdate = true) {
    const { onChange } = this.props
    if (this.refInput && this.maskLib) {
      const newValue = this.maskLib.getValue()
      const { value: prevValue } = this.state
      this.setState({ value: newValue == this.maskLib.emptyValue ? '' : newValue }, () => {
        if (document.activeElement == this.refInput) {
          setSelection(this.refInput, this.maskLib.selection)
        }
        if (sendUpdate && 'function' == typeof onChange && prevValue != newValue) {
          const raw = this.maskLib.getRawValue()
          const { sub } = this.props
          const withSub =
            null != sub
              ? newValue
                  .split('')
                  .map((c, i) => (' ' == c ? sub[i] || ' ' : c))
                  .join('')
              : newValue
          const prevWithSub =
            null != sub
              ? prevValue
                  .split('')
                  .map((c, i) => (' ' == c ? sub[i] || ' ' : c))
                  .join('')
              : prevValue
          onChange(newValue, prevValue, { raw, withSub, prevWithSub, empty: newValue == this.maskLib.emptyValue })
        }
      })
    }
  }

  setMask(props = this.props) {
    const rawValue = this.maskLib ? this.maskLib.getRawValue() : ''
    const { pattern, placeholderChar, formatCharacters } = props
    this.maskLib = new InputMask({
      pattern: pattern,
      placeholderChar: placeholderChar || ' ',
      formatCharacters: formatCharacters,
      value: rawValue
    })
    this.updateValue(false)
  }

  onKeyDown(event) {
    const { onKeyDown } = this.props
    if ('function' == typeof onKeyDown) onKeyDown(event)
    if ('Backspace' == event.key || KEYCODE_BACKSPACE == event.which || KEYCODE_BACKSPACE == event.keyCode) {
      stopEvent(event)
      const { maskLib } = this
      this.updateSelection()
      if (maskLib && maskLib.backspace()) this.updateValue()
      return false
    } else if (isKeyUndo(event)) {
      stopEvent(event)
      const { maskLib } = this
      if (maskLib && maskLib.undo()) this.updateValue()
      return false
    } else if (isKeyRedo(event)) {
      stopEvent(event)
      const { maskLib } = this
      if (maskLib && maskLib.redo()) this.updateValue()
      return false
    }
    return true
  }

  onKeyPress(event) {
    const { onKeyPress } = this.props
    if ('function' == typeof onKeyPress) onKeyPress(event)
    if (event.metaKey || event.altKey || event.ctrlKey || 'Enter' == event.key) return
    stopEvent(event)
    const keyChar = event.key || String.fromCharCode(event.which || event.keyCode)
    this.updateSelection()
    const { maskLib } = this
    if (maskLib && maskLib.input(keyChar)) this.updateValue()
  }

  onPaste(event) {
    const { onPaste } = this.props
    if ('function' == typeof onPaste) onPaste(event)
    const text = event.clipboardData ? event.clipboardData.getData('Text') : null
    if (text) {
      stopEvent(event)
      this.updateSelection()
      const { sub, pattern } = this.props
      if (this.maskLib) {
        const subCorrectedText =
          null == sub
            ? text
            : List(text.split(''))
                .map((c, i) => (c == sub[i] && ' ' == pattern[i] ? ' ' : c))
                .join('')
        if (0 < List(subCorrectedText.split('')).reduce((sum, c) => (this.maskLib.input(c) ? sum + 1 : sum), 0)) {
          this.updateValue()
        }
      }
      return false
    }
    return true
  }

  onChange() {
    if (this.maskLib && this.refInput) {
      const newValue = this.refInput.value
      if (newValue != this.maskLib.getValue()) {
        const { start } = getSelection(this.refInput)
        this.maskLib.setValue(newValue)
        const setTo = Math.min(newValue.length - 1, start)
        this.maskLib.setSelection({ start: setTo, end: setTo })
        setSelection(this.refInput, this.maskLib.selection)
        setTimeout(this.updateValue, 0)
      }
    }
  }

  updatePattern(pattern) {
    const { maskLib, refInput } = this
    if (maskLib && refInput) {
      const preRaw = maskLib.getRawValue().replace(new RegExp(`[${maskLib.placeholderChar}]+$`), '')
      maskLib.setPattern(pattern, { value: '' })
      // reinsert previous text
      List(preRaw.split('')).reduce((sum, c) => (maskLib.input(c) ? sum + 1 : sum), 0)
      this.updateValue(false)
    }
  }

  UNSAFE_componentWillMount() {
    super.UNSAFE_componentWillMount()
    this.setMask()
    const initValue = this.props.value || this.props.defaultValue
    if (null != initValue) {
      this.maskLib.setValue(initValue)
    }
  }

  componentDidMount() {
    super.componentDidMount()
    this.updateValue(false)
  }

  componentWillUnmount() {
    super.componentWillUnmount()
    this.maskLib = null
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    super.UNSAFE_componentWillReceiveProps(nextProps)
    if (
      this.maskLib &&
      null != nextProps.value &&
      nextProps.value != this.props.value &&
      nextProps.value != this.state.value
    ) {
      this.maskLib.setValue(nextProps.value)
      this.updateValue(false)
    }
    if (
      nextProps.placeholderChar != this.props.placeholderChar ||
      nextProps.formatCharacters != this.props.formatCharacters
    ) {
      this.setMask(nextProps)
    } else if (this.maskLib && nextProps.pattern != this.props.pattern) {
      this.updatePattern(nextProps.pattern)
    }
  }

  render() {
    const {
      pattern,
      sub,
      formatCharacters,
      placeholderChar,
      mono,
      width,
      style,
      className,
      placeholder,
      onKeyDown,
      onKeyPress,
      onChange,
      onPaste,
      value,
      defaultValue,
      ...otherProps
    } = this.props
    const managedValue = this.state.value
    const formattedSub = sub
      ? managedValue
        ? sub
            .split('')
            .map((_c, i) => ((placeholderChar || ' ') == managedValue[i] || ' ' == managedValue[i] ? sub[i] : ' '))
            .join('')
        : sub
      : null
    const showSub = null != sub && (!placeholder || managedValue.length > 0)
    return (
      <div
        className={classNames('rfd-ui-masked-input', { 'rfd-ui-masked-input-mono': null != sub || mono })}
        style={{ width }}
      >
        <input
          ref={this.saveRef('Input')}
          key="input"
          type="text"
          className={className}
          value={managedValue}
          style={style}
          onKeyDown={this.onKeyDown}
          onKeyPress={this.onKeyPress}
          onChange={this.onChange}
          onPaste={this.onPaste}
          placeholder={placeholder}
          {...otherProps}
        />
        {showSub && (
          <div className="rfd-ui-masked-input-sub" style={style} key="sub">
            {formattedSub}
          </div>
        )}
      </div>
    )
  }
}
